import React, { useEffect, useRef, useState } from 'react';

import { PrimaryButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import IconCopyLink from '@components/Images/Rebranding/IconCopyLink';
import TIModal, { TiModalColorScheme } from '@components/TIModal';
import useArticleShareLink from '@hooks/useArticleShareLink';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideSafeHook from '@hooks/useServerSideSafeHook';
import { ClickTarget, TrackingLocation, click } from '@tracking';

interface ShareArticleModalProps {
  onClose: () => void;
  isOpen: boolean;
  articleId: number;
}

const ShareArticleModal = ({ isOpen, onClose, articleId }: ShareArticleModalProps) => {
  const articleShareServer = {
    canShareArticle: false,
    escapedArticleTitle: '',
    escapedGoLink: '',
    shareUrl: '',
  };

  const [copied, setCopied] = useState<boolean>(false);
  const timerRef = useRef(null);
  const { articleShare, isArticleShareLoading } = useServerSideSafeHook(useArticleShareLink, {
    articleShare: articleShareServer,
    isArticleShareLoading: true,
  })({ articleId: articleId });
  const { trackEvent } = useEventTracking({ location: TrackingLocation.ShareModal });

  const copyLink = () => {
    trackEvent(click({ clickTarget: ClickTarget.CopyShareLink }));
    navigator.clipboard.writeText(articleShare.shareUrl);
    setCopied(true);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleCloseModal = () => {
    trackEvent(click({ clickTarget: ClickTarget.Close }));
    onClose();
  };

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  return (
    <TIModal
      isOpen={isOpen}
      onClose={onClose}
      customHeader
      isFullScreen
      colorScheme={TiModalColorScheme.dark}
      parentClass="w-full max-w-full xs:max-w-sm sm:max-w-xl md:max-w-5xl lg:max-w-container-lg"
    >
      {isArticleShareLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          <IconClose
            className="absolute right-8 top-8 h-[26px] w-[26px] cursor-pointer text-white sm:right-16 sm:top-16"
            onClick={handleCloseModal}
            data-testid="close-modal"
            strokeWidth="1"
          />
          <div className="flex w-full flex-col items-center justify-center gap-32 px-24 xs:gap-56 xs:py-72 sm:gap-80 sm:px-64 sm:py-96 md:flex-row md:gap-112 md:pb-[128px] md:pl-48 md:pr-90 md:pt-80 lg:gap-[192px]">
            {articleShare?.canShareArticle ? (
              <>
                <div className="flex flex-col">
                  <h1 className="mb-16 mt-0 font-suisse-works text-headline-lg text-white md:text-headline-xl">
                    Share free article
                  </h1>
                  <p className="m-0 font-suisse-intl-mono text-overline-lg text-white">
                    Because you’re a subscriber, you can share this article with anyone (3 reads).
                  </p>
                </div>
                <PrimaryButton
                  as="button"
                  onClick={copyLink}
                  className="copy-link flex w-full shrink-0 items-center md:w-[300px] lg:w-[384px]"
                >
                  <IconCopyLink className="mr-8 w-16 text-white" />
                  {copied ? 'Copied Link' : 'Copy link to free article'}
                </PrimaryButton>
              </>
            ) : (
              <div className="flex flex-col">
                <p className="m-0 font-suisse-intl-mono text-overline-lg text-white">
                  Sorry, you have reached your max number of shares for this article
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </TIModal>
  );
};

export default ShareArticleModal;
